import { useEffect, useRef } from "react";

/**
 * Browser and Android back close the top sheet first, then the route (gym-app: native feel).
 *
 * While any sheet is open there is exactly one extra history entry (a sentinel carrying the router's own state, so React
 * Router sees no navigation). Back pops it and the top sheet closes; if sheets remain beneath, the sentinel is pushed
 * again. Closing a sheet any other way (Esc, the scrim, a tap) lets go of the sentinel, but only after the current render
 * has settled and only if no sheet is left: swapping one sheet for another in the same tick must not eat a history entry.
 */
interface Entry { close: () => void }
const stack: Entry[] = [];
/** Token of the sentinel entry we pushed, or null when there is none. A fresh token per push tells our entry from any stale one. */
let sentinel: number | null = null;
let seq = 0;
let wired = false;

const token = () => (window.history.state as { helmrSheet?: number } | null)?.helmrSheet;

function ensure() {
  if (sentinel !== null) return;
  sentinel = ++seq;
  window.history.pushState({ ...(window.history.state as object | null), helmrSheet: sentinel }, "");
}

function release() {
  if (stack.length > 0 || sentinel === null) return;
  const mine = sentinel;
  sentinel = null;
  if (token() === mine) window.history.back();
}

/**
 * Call right before a sheet navigates, and navigate with `replace: true`: the new route takes the sentinel's history slot.
 * Otherwise `release` races the router, and the sentinel stays behind as a back press that does nothing.
 */
export function handOverSentinel() { sentinel = null; }

function wire() {
  if (wired || typeof window === "undefined") return;
  wired = true;
  window.addEventListener("popstate", () => {
    if (sentinel === null || token() === sentinel) return;
    sentinel = null;
    stack.pop()?.close();
    if (stack.length > 0) ensure();
  });
}

export function useSheetBack(open: boolean, close: () => void) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    wire();
    const entry: Entry = { close: () => closeRef.current() };
    stack.push(entry);
    ensure();
    return () => {
      const i = stack.indexOf(entry);
      if (i === -1) return;
      stack.splice(i, 1);
      setTimeout(release, 0);
    };
  }, [open]);
}
